import { css } from '@emotion/react';
import { HTMLAttributes } from 'react';
import { ContextProps } from './FormControlContext';
import { withForm } from './withForm';

export interface HintProps extends HTMLAttributes<HTMLElement>, ContextProps {
  invalidMessage?: string;
  completedMessage?: string;
  disabledMessage?: string;
  verifiedMessage?: string;
}

const applyMainStyles =
  ({
    isInvalid,
    isCompleted,
    isDisabled,
    isVerified,
  }: Pick<
    HintProps,
    'isInvalid' | 'isCompleted' | 'isDisabled' | 'isVerified'
  >) =>
  (theme) => {
    const defaultStyle = css(theme.form.hint.defaultStyles);

    const styles = [defaultStyle];

    if (isInvalid) {
      styles.push(css(theme.form.hint.isInvalid));
    }
    if (isCompleted) {
      styles.push(css(theme.form.hint.isCompleted));
    }
    if (isDisabled) {
      styles.push(css(theme.form.hint.isDisabled));
    }
    if (isVerified) {
      styles.push(css(theme.form.hint.isVerified));
    }

    return css(...styles);
  };

const evaluateRender = ({
  children,
  isInvalid,
  isCompleted,
  isDisabled,
  isVerified,
  invalidMessage,
  completedMessage,
  disabledMessage,
  verifiedMessage,
  controlId,
  ...props
}) => {
  const commonProps = {
    css: applyMainStyles({ isInvalid, isCompleted, isDisabled, isVerified }),
    id: controlId,
    ...props,
  };

  if (children !== null) {
    return <small {...commonProps}>{children}</small>;
  }

  if (isInvalid && invalidMessage !== undefined) {
    return <small {...commonProps}>{invalidMessage}</small>;
  }

  if (isCompleted && completedMessage !== undefined) {
    return <small {...commonProps}>{completedMessage}</small>;
  }

  if (isDisabled && disabledMessage !== undefined) {
    return <small {...commonProps}>{disabledMessage}</small>;
  }

  if (isVerified && verifiedMessage !== undefined) {
    return <small {...commonProps}>{verifiedMessage}</small>;
  }

  return null;
};

const InternalHint = ({
  children = null,
  isInvalid,
  isCompleted,
  isDisabled,
  isVerified,
  invalidMessage,
  completedMessage,
  disabledMessage,
  verifiedMessage,
  controlId,
  ...props
}: HintProps) =>
  evaluateRender({
    children,
    isInvalid,
    isCompleted,
    isDisabled,
    isVerified,
    invalidMessage,
    completedMessage,
    disabledMessage,
    verifiedMessage,
    controlId,
    ...props,
  });

export const Hint = withForm<HintProps>(InternalHint);
